import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { AUTH_ACCESS_KEY, AUTH_REFRESH_KEY } from '@/constants/auth-storage'
import { env } from '@/config/env'

const ACCESS = AUTH_ACCESS_KEY
const REFRESH = AUTH_REFRESH_KEY

const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(ACCESS)
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (v?: string | null) => void
  reject: (e?: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(token)
  })
  failedQueue = []
}

function isStaffLoginRequest(config: InternalAxiosRequestConfig): boolean {
  const url = config.url || ''
  return (
    config.method?.toLowerCase() === 'post'
    && url.includes('/api/v1/authentication/staff')
  )
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status !== 401 || !original) {
      return Promise.reject(error)
    }

    if (original.url?.includes('/api/v1/auth-refresh')) {
      await invalidateSessionAndRedirect()
      return Promise.reject(error)
    }

    if (isStaffLoginRequest(original)) {
      return Promise.reject(error)
    }

    if (original._retry) {
      await invalidateSessionAndRedirect()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((token) => {
          if (original.headers && token) {
            original.headers.Authorization = `Bearer ${token}`
          }
          return apiClient(original)
        })
        .catch((err) => Promise.reject(err))
    }

    original._retry = true
    isRefreshing = true
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()

    try {
      await authStore.refreshAccessToken()
      const newToken = authStore.token
      processQueue(null, newToken || null)
      if (original.headers && newToken) {
        original.headers.Authorization = `Bearer ${newToken}`
      }
      return apiClient(original)
    } catch (refreshErr) {
      processQueue(refreshErr, null)
      await invalidateSessionAndRedirect()
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

async function invalidateSessionAndRedirect() {
  const { useAuthStore } = await import('@/stores/auth')
  useAuthStore().clear()
  redirectLogin()
}

function redirectLogin() {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  const path = `${base}/login`.replace(/\/+/g, '/')
  if (!window.location.pathname.endsWith('/login')) {
    window.location.assign(path)
  }
}

export default apiClient
